<template>
  <div class="page-wrapper">
    <div class="content container-fluid">
      <div class="page-header">
        <div class="row align-items-center">
          <div class="col">
            <h3 class="page-title mb-1">Bài viết trên website</h3>
            <ul class="breadcrumb">
              <li class="breadcrumb-item"><a href="/">Dashboard</a></li>
              <li class="breadcrumb-item active">Bài viết trên website</li>
            </ul>
          </div>
        </div>
      </div>

      <div class="row filter-row">
        <div class="col-12">
          <div class="card shadow-sm border-0 rounded-lg">
            <div class="card-body pt-3 pb-2">
              <div class="form-row">
                <div class="col-lg-3 col-md-6 mb-3">
                  <label class="text-muted small font-weight-bold mb-1">
                    <i class="fa fa-globe text-primary mr-1"></i> Website
                  </label>
                  <select
                    class="form-control form-control-sm"
                    v-model="selectedSiteId"
                    @change="fetchPosts"
                    ref="siteSelect"
                  >
                    <option disabled value="">-- Chọn một trang web --</option>
                    <option value="all">🌍 Tất cả website</option>
                    <option
                      v-for="site in wpSites"
                      :key="site.id"
                      :value="site.id"
                    >
                      {{ site.name }} ({{ site.baseUrl }})
                    </option>
                  </select>
                </div>
                <div class="col-lg-3 col-md-6 mb-3" v-if="selectedSiteId">
                  <label class="text-muted small font-weight-bold mb-1"
                    >Tìm kiếm</label
                  >
                  <input
                    type="text"
                    v-model="searchKeyword"
                    class="form-control form-control-sm"
                    placeholder="Nhập từ khóa..."
                  />
                </div>
                <div class="col-lg-2 col-md-6 mb-3" v-if="selectedSiteId">
                  <label class="text-muted small font-weight-bold mb-1"
                    >Trạng thái</label
                  >
                  <select
                    v-model="selectedStatus"
                    class="form-control form-control-sm"
                  >
                    <option value="">Tất cả</option>
                    <option value="publish">Đã xuất bản</option>
                    <option value="draft">Bản nháp</option>
                  </select>
                </div>
                <div class="col-lg-2 col-md-6 mb-3" v-if="selectedSiteId">
                  <label class="text-muted small font-weight-bold mb-1"
                    >Từ ngày</label
                  >
                  <input
                    type="date"
                    v-model="filterStartDate"
                    class="form-control form-control-sm"
                  />
                </div>
                <div class="col-lg-2 col-md-6 mb-3" v-if="selectedSiteId">
                  <label class="text-muted small font-weight-bold mb-1"
                    >Đến ngày</label
                  >
                  <input
                    type="date"
                    v-model="filterEndDate"
                    class="form-control form-control-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row mt-4">
        <div v-if="postsLoading" class="col-12 text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Đang tải...</span>
          </div>
        </div>
        <div
          v-else-if="paginatedPosts.length === 0"
          class="col-12 text-center text-muted py-5"
        >
          Không có bài viết nào
        </div>
        <div
          v-else
          class="col-lg-4 col-md-6 mb-4"
          v-for="post in paginatedPosts"
          :key="post.id"
        >
          <div class="card post-card h-100 shadow-sm">
            <div class="card-body d-flex flex-column">
              <h5 class="card-title text-truncate">
                <a :href="post.link" target="_blank">{{
                  post.title.rendered
                }}</a>
              </h5>
              <ul class="list-unstyled mb-3 flex-grow-1">
                <li class="text-secondary">
                  <i class="fa fa-tag mr-2"></i
                  >{{ post.mainKeyword || "Không có" }}
                </li>
                <li class="text-secondary">
                  <i class="fa fa-calendar-alt mr-2"></i
                  >{{ formatDate(post.date) }}
                </li>
                <li class="mt-2">
                  <span
                    v-if="post.status === 'publish'"
                    class="badge badge-success"
                    >Xuất bản</span
                  >
                  <span v-else class="badge badge-secondary">Nháp</span>
                </li>
              </ul>
              <div
                class="d-flex justify-content-between align-items-center mt-auto"
              >
                <span
                  :class="[
                    'badge',
                    post.meta && post.meta.rank_math_seo_score >= 80
                      ? 'badge-success'
                      : post.meta && post.meta.rank_math_seo_score >= 50
                      ? 'badge-warning'
                      : 'badge-danger',
                  ]"
                >
                  SEO:
                  {{
                    post.meta && post.meta.rank_math_seo_score
                      ? post.meta.rank_math_seo_score
                      : 0
                  }}
                </span>
                <div class="post-actions">
                  <a
                    :href="'https://www.google.com/search?q=site:' + post.link"
                    target="_blank"
                    class="btn btn-sm btn-info mr-2"
                    title="Kiểm tra index"
                  >
                    <i class="fa fa-search"></i>
                  </a>
                  <button
                    class="btn btn-sm btn-success mr-2"
                    @click="openFacebookShareModal(post)"
                    title="Đăng lên Facebook"
                  >
                    <i class="fa fa-facebook-square"></i>
                  </button>
                  <button
                    class="btn btn-sm btn-primary"
                    @click="startEditing(post)"
                    title="Chỉnh sửa"
                  >
                    <i class="fa fa-pencil"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="filteredPosts.length > itemsPerPage" class="row">
        <div
          class="col-md-12 d-flex justify-content-between align-items-center mt-4"
        >
          <div>
            <small class="text-muted"
              >Đang hiển thị {{ paginatedPosts.length }} trên tổng số
              {{ filteredPosts.length }} bài viết.</small
            >
          </div>
          <nav>
            <ul
              class="pagination justify-content-center align-items-center mb-0"
            >
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <a
                  class="page-link"
                  href="#"
                  @click.prevent="changePage(currentPage - 1)"
                  >Trước</a
                >
              </li>
              <li class="page-item mx-2 d-flex align-items-center">
                <input
                  type="number"
                  v-model.lazy="inputPage"
                  @keyup.enter="goToPage"
                  class="form-control form-control-sm text-center"
                  style="width: 70px"
                />
                <span class="text-muted ml-2">/ {{ totalPages }}</span>
              </li>
              <li
                class="page-item"
                :class="{ disabled: currentPage === totalPages }"
              >
                <a
                  class="page-link"
                  href="#"
                  @click.prevent="changePage(currentPage + 1)"
                  >Sau</a
                >
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  </div>

  <div
    class="modal fade"
    id="facebook_share_modal"
    tabindex="-1"
    ref="facebookShareModal"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Đăng bài lên Facebook</h5>
          <button type="button" class="close" data-dismiss="modal">
            &times;
          </button>
        </div>
        <div class="modal-body">
          <div v-if="fanpageLoading" class="text-center text-muted">
            Đang tải danh sách Fanpage...
          </div>
          <div
            v-else-if="userFanpages.length === 0"
            class="text-center text-muted"
          >
            Không tìm thấy Fanpage nào.
          </div>
          <div v-else class="form-group">
            <label for="fanpage-select">Chọn Fanpage</label>
            <select
              id="fanpage-select"
              v-model="selectedFanpageId"
              class="form-control"
            >
              <option
                v-for="page in userFanpages"
                :key="page.id"
                :value="page.id"
              >
                {{ page.name }}
              </option>
            </select>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-dismiss="modal">
            Hủy
          </button>
          <button
            class="btn btn-primary"
            @click="sharePostToFacebook"
            :disabled="!selectedFanpageId"
          >
            Đăng bài
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  onMounted,
  computed,
  watch,
  nextTick,
  onBeforeUnmount,
} from "vue";
import { useRouter } from "vue-router";
import api from "../api";
import { useToast } from "vue-toastification";

const wpSites = ref([]);
const selectedSiteId = ref("");
const searchKeyword = ref("");
const selectedStatus = ref("");
const filterStartDate = ref("");
const filterEndDate = ref("");
const posts = ref([]);
const loading = ref(true);
const postsLoading = ref(false);
const router = useRouter();
const toast = useToast();

const siteSelect = ref(null);

const currentPage = ref(1);
const itemsPerPage = ref(9);
const inputPage = ref(1);

// Biến cho tính năng Facebook
const facebookShareModal = ref(null);
const postToShare = ref(null);
const userFanpages = ref([]);
const selectedFanpageId = ref("");
const fanpageLoading = ref(false);

onMounted(async () => {
  try {
    const res = await api.get(`/api/wp-sites`);
    wpSites.value = res.data.content;

    await nextTick();
    if (siteSelect.value && window.$) {
      $(siteSelect.value).select2({
        theme: "bootstrap4",
        minimumResultsForSearch: -1,
        width: "100%",
      });
      $(siteSelect.value).on("change", function () {
        selectedSiteId.value = $(this).val();
        fetchPosts();
      });
    }
  } catch (err) {
    toast.error("Lỗi tải danh sách website. Vui lòng kiểm tra kết nối.");
    console.error(err);
  } finally {
    loading.value = false;
  }
});

onBeforeUnmount(() => {
  if (siteSelect.value && window.$ && $(siteSelect.value).data("select2")) {
    $(siteSelect.value).off("change").select2("destroy");
  }
});

const fetchPosts = async () => {
  if (!selectedSiteId.value) {
    posts.value = [];
    return;
  }

  postsLoading.value = true;
  posts.value = [];
  currentPage.value = 1;

  try {
    let res;
    if (selectedSiteId.value === "all") {
      res = await api.get(`/api/wp-sites/posts/all`);
      toast.info(
        "Đang tải tất cả bài viết từ các website. Thao tác này có thể mất một lúc."
      );
    } else {
      res = await api.get(`/api/wp-sites/${selectedSiteId.value}/posts/all`);
    }

    posts.value = res.data.map((post) => {
      const focusKeywordString = post.meta?.["rank_math_focus_keyword"] || "";
      let mainKeyword = "";
      if (focusKeywordString) {
        const keywordsArray = focusKeywordString.split(",");
        mainKeyword = keywordsArray[0];
      }
      return { ...post, mainKeyword };
    });
  } catch (err) {
    toast.error("Lỗi tải bài viết. Vui lòng thử lại.");
    console.error(err);
  } finally {
    postsLoading.value = false;
  }
};

const filteredPosts = computed(() => {
  const keyword = (searchKeyword.value || "").toLowerCase();
  const startDateTime = filterStartDate.value
    ? new Date(filterStartDate.value)
    : null;
  const endDateTime = filterEndDate.value
    ? new Date(filterEndDate.value)
    : null;
  if (endDateTime) endDateTime.setDate(endDateTime.getDate() + 1);

  return posts.value.filter((post) => {
    const title = post.title?.rendered?.toLowerCase() || "";
    const mainKeyword = post.mainKeyword?.toLowerCase() || "";
    const postDate = new Date(post.date);

    const matchesKeyword =
      !keyword || title.includes(keyword) || mainKeyword.includes(keyword);
    const matchesStatus =
      !selectedStatus.value || post.status === selectedStatus.value;
    const matchesDateRange =
      (!startDateTime || postDate >= startDateTime) &&
      (!endDateTime || postDate < endDateTime);

    return matchesKeyword && matchesStatus && matchesDateRange;
  });
});

const paginatedPosts = computed(() => {
  const startIndex = (currentPage.value - 1) * itemsPerPage.value;
  return filteredPosts.value.slice(startIndex, startIndex + itemsPerPage.value);
});

const totalPages = computed(() =>
  Math.ceil(filteredPosts.value.length / itemsPerPage.value)
);

const changePage = (pageNumber) => {
  if (pageNumber >= 1 && pageNumber <= totalPages.value)
    currentPage.value = pageNumber;
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  const options = { year: "numeric", month: "2-digit", day: "2-digit" };
  return new Date(dateString).toLocaleDateString("vi-VN", options);
};

watch([searchKeyword, selectedStatus, filterStartDate, filterEndDate], () => {
  currentPage.value = 1;
});

// Logic mới cho ô nhập liệu phân trang
watch(currentPage, (newPage) => {
  inputPage.value = newPage;
});

const goToPage = () => {
  let page = parseInt(inputPage.value);
  if (!isNaN(page) && page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
  } else {
    toast.error(
      "Số trang không hợp lệ. Vui lòng nhập số từ 1 đến " + totalPages.value
    );
  }
};

const startEditing = (post) => {
  router.push(`/edit-article/${post.id}/${selectedSiteId.value}`);
};

const openFacebookShareModal = async (post) => {
  postToShare.value = post;
  userFanpages.value = [];
  selectedFanpageId.value = "";
  fanpageLoading.value = true;

  try {
    const res = await api.get(`/api/fanpages`);
    userFanpages.value = res.data;
    if (userFanpages.value.length > 0) {
      selectedFanpageId.value = userFanpages.value[0].id;
    }
  } catch (err) {
    toast.error("Lỗi tải danh sách Fanpage.");
    console.error(err);
  } finally {
    fanpageLoading.value = false;
  }

  if (window.$ && facebookShareModal.value) {
    $(facebookShareModal.value).modal("show");
  }
};

const sharePostToFacebook = async () => {
  if (!selectedFanpageId.value || !postToShare.value) {
    toast.error("Vui lòng chọn một Fanpage và bài viết.");
    return;
  }

  const selectedFanpage = userFanpages.value.find(
    (p) => p.id === selectedFanpageId.value
  );
  if (!selectedFanpage || !selectedFanpage.token) {
    toast.error("Không tìm thấy token của Fanpage đã chọn.");
    return;
  }

  try {
    const n8nWebhookUrl = process.env.VUE_APP_WEBHOOK_BLOGONWEBSVEW_URL;
    // const n8nWebhookUrl = 'https://n8n.thepminhtien.com/webhook/7b222891-8ef3-4131-8ce3-910a781e81cc';

    const payload = {
      fanpageId: selectedFanpageId.value,
      fanpageToken: selectedFanpage.token,
      postUrl: postToShare.value.link,
      maTrang: selectedFanpage.maTrang,
    };

    await api.post(n8nWebhookUrl, payload);

    toast.success(
      "Yêu cầu đăng bài đã được gửi đi. Vui lòng kiểm tra Fanpage."
    );

    if (window.$ && facebookShareModal.value) {
      $(facebookShareModal.value).modal("hide");
    }
  } catch (error) {
    toast.error("Có lỗi xảy ra khi gửi yêu cầu đến n8n.");
    console.error("Lỗi khi gửi webhook:", error);
  }
};
</script>

<style scoped>
/* CSS cũ của bạn đã được giữ nguyên và bổ sung */
.post-card {
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  border-radius: 10px;
}

.post-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}

.card-title a {
  color: #495057;
  font-size: 1.2rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-decoration: none;
  transition: color 0.2s;
}

.card-title a:hover {
  color: #007bff;
}

.card-body ul li {
  font-size: 0.9rem;
  color: #6c757d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.card-body ul li .fa {
  width: 1rem;
  text-align: center;
}

.post-actions .btn {
  font-size: 0.8rem;
  padding: 0.25rem 0.5rem;
}

/* Đều hàng filter (Select2 khớp chiều cao .form-control-sm ~31px) */
.select2-container--bootstrap4 .select2-selection--single {
  height: 31px !important;
  min-height: 31px !important;
  padding: 0.25rem 0.5rem !important;
  border-radius: 0.2rem !important;
}
.select2-container--bootstrap4 .select2-selection__rendered {
  line-height: 30px !important;
  font-size: 0.875rem;
}
.select2-container--bootstrap4 .select2-selection__arrow {
  height: 30px !important;
}

/* Pagination responsive */
.card-footer nav {
  overflow-x: auto;
}

.pagination {
  white-space: nowrap;
}

.pagination .page-item {
  flex: 0 0 auto; /* không co dãn khi scroll */
}
</style>
